import React, { useState, useEffect } from 'react';
import { createPageUrl } from '../../utils/index.ts';
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { Button } from "../ui/button.jsx";
import { Skeleton } from "../ui/skeleton.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { Progress } from "../ui/progress.jsx";
import { 
  Calendar, 
  Users, 
  ArrowRight, 
  Building,
  GripHorizontal,
  CheckCircle2,
  Clock,
  PauseCircle,
  Lightbulb,
  AlertTriangle,
  MoreHorizontal,
  Star,
  Edit,
  Eye,
  Target,
  Zap,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger 
} from "../ui/tooltip.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.jsx";

// Enhanced status configuration
const STATUS_CONFIG = {
  planning: {
    label: 'Planejamento',
    icon: Lightbulb,
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    bgGradient: 'from-violet-50 to-purple-50',
    borderColor: 'border-purple-200',
    accentColor: 'bg-purple-500',
    iconColor: 'text-purple-600'
  },
  in_progress: {
    label: 'Em Andamento',
    icon: Clock,
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    bgGradient: 'from-blue-50 to-indigo-50',
    borderColor: 'border-blue-200',
    accentColor: 'bg-blue-500',
    iconColor: 'text-blue-600'
  },
  paused: {
    label: 'Pausado',
    icon: PauseCircle,
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    bgGradient: 'from-amber-50 to-orange-50',
    borderColor: 'border-orange-200',
    accentColor: 'bg-orange-500',
    iconColor: 'text-orange-600'
  },
  completed: {
    label: 'Concluído',
    icon: CheckCircle2,
    color: 'bg-green-100 text-green-800 border-green-300',
    bgGradient: 'from-emerald-50 to-green-50',
    borderColor: 'border-green-200',
    accentColor: 'bg-green-500',
    iconColor: 'text-green-600'
  }
};

export default function ProjectsKanban({ projects, clients, isLoading, onViewProject, navigate }) {
  // Group projects by status for initial state
  const [groupedProjects, setGroupedProjects] = useState(() => {
    const grouped = {
      planning: [],
      in_progress: [],
      paused: [],
      completed: []
    };
    
    projects.forEach(project => {
      if (grouped[project.status]) {
        grouped[project.status].push(project);
      }
    });
    
    return grouped;
  });

  // Update grouped projects when projects prop changes
  React.useEffect(() => {
    const grouped = {
      planning: [],
      in_progress: [],
      paused: [],
      completed: []
    };
    
    projects.forEach(project => {
      if (grouped[project.status]) {
        grouped[project.status].push(project);
      }
    });
    
    setGroupedProjects(grouped);
  }, [projects]);

  // Function to update project status when dropped
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Do nothing if dropped outside a droppable area
    if (!destination) return;

    // Do nothing if dropped in the same place
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) return;

    // Get the project from source
    const projectId = draggableId;
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Get new status
    const newStatus = destination.droppableId;
    if (project.status === newStatus) return;

    // Update local state first for responsiveness
    const sourceProjects = [...groupedProjects[source.droppableId]];
    const destinationProjects = [...groupedProjects[destination.droppableId]];
    
    // Remove from source
    const [removed] = sourceProjects.splice(source.index, 1);
    
    // Add to destination
    destinationProjects.splice(destination.index, 0, { ...removed, status: newStatus });
    
    // Update state
    setGroupedProjects({
      ...groupedProjects,
      [source.droppableId]: sourceProjects,
      [destination.droppableId]: destinationProjects
    });

    // Update in database
    try {
      await Project.update(projectId, { status: newStatus });
      console.log(`✅ Projeto ${project.name} movido para ${STATUS_CONFIG[newStatus].label}`);
    } catch (error) {
      console.error('❌ Erro ao atualizar status do projeto:', error);
      // Revert UI if fails
      alert('Erro ao atualizar status do projeto. Tente novamente.');
      setGroupedProjects({
        ...groupedProjects
      });
    }
  };

  const getClientName = (clientId) => {
    if (!clientId || !clients || !Array.isArray(clients)) return "Cliente não encontrado";
    const client = clients.find(c => c.id === clientId);
    return client ? (client.corporate_name || client.name) : 'Cliente não encontrado';
  };

  // Calculate project progress based on status
  const getProjectProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'in_progress') return 60;
    if (project.status === 'planning') return 20;
    if (project.status === 'paused') return 40;
    return 0;
  };

  // Calculate days remaining or overdue
  const getDaysIndicator = (project) => {
    if (!project.end_date) return null;
    
    const endDate = new Date(project.end_date);
    const today = new Date();
    
    // Set hours to 0 for accurate day calculation
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { 
        text: `${Math.abs(diffDays)} dia${Math.abs(diffDays) > 1 ? 's' : ''} atrasado`, 
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: AlertTriangle
      };
    } else if (diffDays === 0) {
      return { 
        text: 'Vence hoje', 
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        icon: AlertTriangle
      };
    } else if (diffDays <= 3) {
      return { 
        text: `${diffDays} dia${diffDays > 1 ? 's' : ''} restante${diffDays > 1 ? 's' : ''}`, 
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        icon: Clock
      };
    } else if (diffDays <= 7) {
      return { 
        text: `${diffDays} dias restantes`, 
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: Clock
      };
    } else {
      return { 
        text: `${diffDays} dias restantes`, 
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: CheckCircle2
      };
    }
  };

  // Get priority indicator (example implementation)
  const getPriorityIndicator = (project) => {
    // You can implement priority logic here based on your project data
    const daysIndicator = getDaysIndicator(project);
    const progress = getProjectProgress(project);
    
    if (daysIndicator && daysIndicator.color === 'text-red-600') {
      return { level: 'high', color: 'bg-red-500', label: 'Alta' };
    } else if (progress < 30 && project.status === 'in_progress') {
      return { level: 'medium', color: 'bg-yellow-500', label: 'Média' };
    } else if (project.status === 'completed') {
      return { level: 'low', color: 'bg-green-500', label: 'Concluído' };
    }
    return { level: 'normal', color: 'bg-gray-400', label: 'Normal' };
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-4">
            <div className="flex items-center gap-2 p-4 rounded-xl bg-gray-50">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-8 ml-auto rounded-full" />
            </div>
            {Array.from({ length: 3 }).map((_, j) => (
              <Skeleton key={j} className="h-48 w-full rounded-xl" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Object.entries(groupedProjects).map(([status, statusProjects]) => {
          const statusConfig = STATUS_CONFIG[status];
          
          return (
            <motion.div 
              key={status} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Enhanced column header */}
              <div className={`${statusConfig.color} p-4 rounded-xl shadow-sm border-2 ${statusConfig.borderColor}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg bg-white/80`}>
                      <statusConfig.icon className={`w-4 h-4 ${statusConfig.iconColor}`} />
                    </div>
                    <h3 className="font-semibold text-sm">{statusConfig.label}</h3>
                  </div>
                  <Badge variant="secondary" className="bg-white/80 text-gray-700 font-medium">
                    {statusProjects.length}
                  </Badge>
                </div>
                
                {/* Progress indicator for the column */}
                {statusProjects.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Progresso médio</span>
                      <span>
                        {Math.round(statusProjects.reduce((acc, p) => acc + getProjectProgress(p), 0) / statusProjects.length)}%
                      </span>
                    </div>
                    <Progress 
                      value={statusProjects.reduce((acc, p) => acc + getProjectProgress(p), 0) / statusProjects.length} 
                      className="h-1.5 bg-white/50" 
                    />
                  </div>
                )}
              </div>
              
              <Droppable droppableId={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-4 min-h-[200px] p-2 rounded-xl transition-all duration-200 ${
                      snapshot.isDraggingOver ? 'bg-blue-50/50 ring-2 ring-blue-200' : ''
                    }`}
                  >
                    <AnimatePresence>
                      {statusProjects.map((project, index) => {
                        const client = clients.find(c => c.id === project.client_id);
                        const daysIndicator = getDaysIndicator(project);
                        const progress = getProjectProgress(project);
                        const priority = getPriorityIndicator(project);
                        
                        return (
                          <Draggable 
                            key={project.id} 
                            draggableId={project.id} 
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`bg-gradient-to-br ${statusConfig.bgGradient} ${statusConfig.borderColor} border-l-4 ${
                                    snapshot.isDragging ? 'shadow-xl ring-4 ring-primary/20 rotate-2 scale-105' : 'shadow-sm hover:shadow-md'
                                  } transition-all duration-200 relative overflow-hidden group`}
                                >
                                  {/* Priority indicator */}
                                  <div className={`absolute top-0 right-0 w-3 h-8 ${priority.color}`}></div>
                                  
                                  <CardHeader className="pb-3 px-4 pt-4">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <div 
                                            {...provided.dragHandleProps}
                                            className="cursor-grab hover:text-primary hover:bg-primary/5 p-1 rounded transition-colors"
                                          >
                                            <GripHorizontal size={14} />
                                          </div>
                                          <CardTitle 
                                            className="text-sm font-semibold hover:text-primary cursor-pointer truncate flex-1" 
                                            onClick={() => onViewProject(project.id)}
                                          >
                                            {project.name}
                                          </CardTitle>
                                        </div>
                                        
                                        {/* Project progress */}
                                        <div className="space-y-1">
                                          <div className="flex items-center justify-between text-xs">
                                            <span className="text-muted-foreground">Progresso</span>
                                            <span className="font-medium text-gray-700">{progress}%</span>
                                          </div>
                                          <Progress value={progress} className="h-1.5" />
                                        </div>
                                      </div>
                                      
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            <MoreHorizontal size={12} />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem onClick={() => onViewProject(project.id)}>
                                            <Eye className="w-4 h-4 mr-2" />
                                            Visualizar
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => navigate(createPageUrl(`EditProject?id=${project.id}`))}>
                                            <Edit className="w-4 h-4 mr-2" />
                                            Editar
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </CardHeader>
                                  
                                  <CardContent className="py-2 px-4 space-y-3 text-sm">
                                    {/* Client info */}
                                    {client && (
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Building size={12} />
                                        <span className="truncate text-xs font-medium">
                                          {client.corporate_name || client.name}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Date and deadline info */}
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Calendar size={12} />
                                        <div className="text-xs">
                                          <span>
                                            {format(new Date(project.start_date), "dd/MM", { locale: ptBR })}
                                          </span>
                                          {project.end_date && (
                                            <>
                                              <span className="mx-1">→</span>
                                              <span>
                                                {format(new Date(project.end_date), "dd/MM", { locale: ptBR })}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Days indicator */}
                                      {daysIndicator && (
                                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${daysIndicator.color} ${daysIndicator.bgColor}`}>
                                          <daysIndicator.icon size={10} />
                                          {daysIndicator.text}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Team info */}
                                    {project.team && project.team.length > 0 && (
                                      <div className="pt-1">
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-1 text-muted-foreground">
                                            <Users size={12} />
                                            <span className="text-xs font-medium">Equipe</span>
                                          </div>
                                          <span className="text-xs text-muted-foreground">
                                            {project.team.length} membro{project.team.length > 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        
                                        {project.team.length <= 4 ? (
                                          <div className="flex -space-x-1">
                                            {project.team.map((member, i) => (
                                              <Tooltip key={i}>
                                                <TooltipTrigger asChild>
                                                  <Avatar className="h-6 w-6 border-2 border-white">
                                                    <AvatarFallback className="text-[9px] font-medium bg-gradient-to-br from-blue-100 to-purple-100">
                                                      {member.split('@')[0].substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                  </Avatar>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom">{member}</TooltipContent>
                                              </Tooltip>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1">
                                            <div className="flex -space-x-1">
                                              {project.team.slice(0, 3).map((member, i) => (
                                                <Tooltip key={i}>
                                                  <TooltipTrigger asChild>
                                                    <Avatar className="h-6 w-6 border-2 border-white">
                                                      <AvatarFallback className="text-[9px] font-medium bg-gradient-to-br from-blue-100 to-purple-100">
                                                        {member.split('@')[0].substring(0, 2).toUpperCase()}
                                                      </AvatarFallback>
                                                    </Avatar>
                                                  </TooltipTrigger>
                                                  <TooltipContent side="bottom">{member}</TooltipContent>
                                                </Tooltip>
                                              ))}
                                            </div>
                                            <div className="h-6 w-6 rounded-full bg-muted border-2 border-white flex items-center justify-center text-[9px] font-medium">
                                              +{project.team.length - 3}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </CardContent>
                                  
                                  <CardFooter className="py-2 px-4 border-t border-gray-100/50">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="w-full justify-between text-xs hover:bg-primary/5 h-8"
                                      onClick={() => onViewProject(project.id)}
                                    >
                                      Ver detalhes
                                      <ArrowRight size={12} />
                                    </Button>
                                  </CardFooter>
                                </Card>
                              </motion.div>
                            )}
                          </Draggable>
                        );
                      })}
                    </AnimatePresence>
                    {provided.placeholder}
                    
                    {/* Empty state for columns */}
                    {statusProjects.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className={`p-3 rounded-xl ${statusConfig.bgGradient} border-2 border-dashed ${statusConfig.borderColor} mb-3`}>
                          <statusConfig.icon className={`w-6 h-6 ${statusConfig.iconColor}`} />
                        </div>
                        <p className="text-sm text-muted-foreground font-medium">
                          Nenhum projeto em {statusConfig.label.toLowerCase()}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Arraste projetos aqui para alterar o status
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </motion.div>
          );
        })}
      </div>
    </DragDropContext>
  );
}